import React, { useEffect, useState } from "react";
import { Modal, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import api from "../../../api";
import {
  CALCULATION_LABELS, DATA_SOURCE_META, EVIDENCE_STATUS_META, FEEDBACK_STATUS_META, STATUS_META, formatFeedbackAverage,
  formatKpiValue, formatMonthKey, formatRatioParts, formatScore, monthKeyFor, monthsForFiscalYear,
} from "./performanceFormat";

// KPI drill-down: rating rule, month-by-month calculation, the underlying
// tickets for automatic ticket KPIs, and the user's own manual entries.
// Raw kpi_manual_evidence.status -> the four states the user sees.
const EVIDENCE_STATUS_LABELS = {
  DRAFT: "Draft", SUBMITTED: "Submitted · Awaiting Review", UNDER_REVIEW: "Submitted · Awaiting Review",
  APPROVED: "Approved", NEEDS_CHANGES: "Changes requested", REJECTED: "Rejected",
};

function Pill({ meta }) {
  return meta ? <span className={`kp-pill kp-pill-${meta.tone}`}>{meta.label}</span> : null;
}

function formatRange(t) {
  if (t.min_value === null && t.max_value === null) return "Any value";
  if (t.min_value === null) return `≤ ${formatScore(t.max_value)}`;
  if (t.max_value === null) return `≥ ${formatScore(t.min_value)}`;
  return `${formatScore(t.min_value)} – ${formatScore(t.max_value)}`;
}

const formatDateTime = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return String(v); }
};

const pct = (v) => (v === null || v === undefined ? "No data" : `${formatScore(v)}%`);

function TicketDrilldown({ tickets }) {
  const [scope, setScope] = useState("month");
  const block = tickets[scope];
  const s = block.summary;
  return (
    <section className="kp-detail-section">
      <h6>SLA summary</h6>
      <div className="kp-sla-grid kp-sla-headline">
        <div><span>Qualifying tickets</span><strong>{tickets.month.summary.total}</strong></div>
        <div><span>Completed within SLA</span><strong className="kp-text-green">{tickets.month.summary.within_sla}</strong></div>
        <div><span>SLA % (month)</span><strong>{pct(tickets.month.summary.percentage)}</strong></div>
        <div><span>YTD SLA %</span><strong>{pct(tickets.ytd.summary.percentage)}</strong></div>
        <div><span>Rating</span><strong>{tickets.rating === null || tickets.rating === undefined ? "—" : `${tickets.rating} / 5`}</strong></div>
      </div>
      <p className="kp-note-inline">{tickets.period_note || "Tickets are counted in the month their SLA due date falls in, not the month they were created."}</p>

      <div className="kp-detail-section-head">
        <h6>Underlying tickets</h6>
        <div className="kp-seg" role="group" aria-label="Ticket period">
          <button type="button" className={scope === "month" ? "active" : ""} onClick={() => setScope("month")}>This month</button>
          <button type="button" className={scope === "ytd" ? "active" : ""} onClick={() => setScope("ytd")}>Year to date</button>
        </div>
      </div>
      <p className="kp-sub mb-2">{tickets.scope_note}</p>
      <div className="kp-sla-grid">
        <div><span>Qualifying</span><strong>{s.total}</strong></div>
        <div><span>Within SLA</span><strong className="kp-text-green">{s.within_sla}</strong></div>
        <div><span>Breached</span><strong className="kp-text-red">{s.breached}</strong></div>
        <div><span>Open</span><strong>{s.open}</strong></div>
        <div><span>Within SLA %</span><strong>{s.percentage === null ? "No data" : `${formatScore(s.percentage)}%`}</strong></div>
      </div>
      {block.rows.length ? (
        <div className="table-responsive kp-table-wrap">
          <Table className="tlp-table kp-table mb-0" size="sm">
            <thead>
              <tr><th>#</th><th>Ticket</th><th>Category</th><th>Status</th><th>Due</th><th>Completed</th><th>SLA</th></tr>
            </thead>
            <tbody>
              {block.rows.map((t) => (
                <tr key={t.id}>
                  <td data-label="#">{t.id}</td>
                  <td data-label="Ticket"><Link to={`../tasks/${t.id}`} relative="path" className="kp-link">{t.title}</Link></td>
                  <td data-label="Category">{[t.category_name, t.sub_category_name].filter(Boolean).join(" › ") || "—"}</td>
                  <td data-label="Status">{t.status}</td>
                  <td data-label="Due">{formatDateTime(t.due_at)}</td>
                  <td data-label="Completed">{formatDateTime(t.completed_at)}</td>
                  <td data-label="SLA">
                    {t.on_time === true && <span className="kp-pill kp-pill-green">Within SLA</span>}
                    {t.on_time === false && <span className="kp-pill kp-pill-red">Breached</span>}
                    {t.on_time === null && <span className="kp-pill kp-pill-gray">Open</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : <p className="kp-muted mb-0">No qualifying tickets due in this period.</p>}
    </section>
  );
}

// Phase 65: this month's operational context + feedback, then the YTD
// feedback and the per-response breakdown. Every figure is the API's
// (resolver + engine); missing feedback is shown as awaiting, never 0.
function MonthContext({ kpi, context, month }) {
  if (!context && !kpi.feedback) return null;
  const fb = kpi.feedback;
  const names = (list) => (list && list.length ? list.map((x) => x.name).join(", ") : "—");
  return (
    <section className="kp-detail-section">
      <h6>{formatMonthKey(month)}</h6>
      <div className="kp-sla-grid">
        <div><span>Sessions conducted</span><strong>{formatKpiValue(kpi.calculation_type, kpi.current_month)}</strong></div>
        {context && <div><span>Schools</span><strong>{names(context.schools)}</strong></div>}
        {context && <div><span>Category</span><strong>{context.category ? context.category.name : "—"}</strong></div>}
        {context && <div><span>Subcategories</span><strong>{names(context.subcategories)}</strong></div>}
        {fb && <div><span>Feedback responses</span><strong>{fb.month.responses}</strong></div>}
        {fb && (
          <div><span>Average feedback</span>
            <strong>{formatFeedbackAverage(fb.month.average, fb.max_score) || <Pill meta={FEEDBACK_STATUS_META[fb.month.status]} />}</strong>
          </div>
        )}
        <div><span>Rating (YTD)</span><strong>{kpi.rating === null ? "—" : `${kpi.rating} / 5`}</strong></div>
      </div>
      {fb && fb.month.status === "REQUEST_FAILED" && (
        <p className="kp-note-inline">The feedback form could not be sent{fb.month.campaign_failure_reason ? ` (${fb.month.campaign_failure_reason})` : ""}. Please contact an administrator.</p>
      )}
      {context && context.feedback_recipients && context.feedback_recipients.length > 0 && (
        <p className="kp-sub mb-0">Feedback requested from: {names(context.feedback_recipients)}</p>
      )}
    </section>
  );
}

function FeedbackSection({ kpi, detail }) {
  const fb = kpi.feedback;
  if (!fb) return null;
  const rows = detail.feedback_detail?.month || [];
  const byMonth = (detail.feedback_detail?.by_month || []).filter((m) => m.status !== "NOT_REQUESTED" || m.responses > 0);
  const avg = (v) => formatFeedbackAverage(v, fb.max_score);
  return (
    <section className="kp-detail-section">
      <h6>User feedback</h6>
      <div className="kp-sla-grid kp-sla-headline">
        <div><span>Feedback responses (YTD)</span><strong>{fb.ytd.responses}</strong></div>
        <div><span>Average feedback (YTD)</span><strong>{avg(fb.ytd.average) || "Awaiting User Feedback"}</strong></div>
        <div><span>Feedback achievement</span><strong>{fb.ytd.achievement_percent === null ? "—" : `${formatScore(fb.ytd.achievement_percent)}%`}</strong></div>
        <div><span>KPI rating</span><strong>{kpi.rating === null ? "—" : `${kpi.rating} / 5`}</strong></div>
      </div>
      {fb.rule && (
        <p className="kp-note-inline">
          Rating rule: sessions decide the rating; a {fb.rule.applies_to_rating} also needs average feedback of at least {formatScore(fb.rule.min_average)} / 5
          {fb.scale && fb.scale.ok && fb.scale.scale_max !== 5 ? ` (this form is scored out of ${formatScore(fb.scale.scale_max)}, compared on a 5-point scale)` : ""}, otherwise it is capped at {fb.rule.capped_rating}.
          {fb.rule.met === null ? " Not assessed yet." : fb.rule.met ? " Met." : " Not met."}
          {fb.rule.rating_capped ? " The rating is currently capped." : ""}
        </p>
      )}
      {fb.scale && !fb.scale.ok && (
        <p className="kp-note-inline text-danger" data-testid="kp-feedback-scale-issue">
          Feedback is not used for the rating: {fb.scale.message || fb.scale.reason}
        </p>
      )}
      {byMonth.length > 0 && (
        <div className="table-responsive kp-table-wrap mb-2">
          <Table className="tlp-table kp-table mb-0" size="sm">
            <thead><tr><th>Month</th><th>Responses</th><th>Average</th><th>Status</th></tr></thead>
            <tbody>
              {byMonth.map((m) => (
                <tr key={m.period_month}>
                  <td data-label="Month">{formatMonthKey(m.period_month)}</td>
                  <td data-label="Responses">{m.responses}</td>
                  <td data-label="Average">{avg(m.average) || "—"}</td>
                  <td data-label="Status"><Pill meta={FEEDBACK_STATUS_META[m.status]} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <h6 className="mt-2">Responses this month</h6>
      {rows.length ? (
        <div className="table-responsive kp-table-wrap">
          <Table className="tlp-table kp-table mb-0" size="sm">
            <thead><tr><th>Respondent</th><th>Submitted</th><th>Score</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.submission_id}>
                  <td data-label="Respondent">{r.respondent_name || "—"}</td>
                  <td data-label="Submitted">{formatDateTime(r.submitted_at)}</td>
                  <td data-label="Score">
                    {r.score === null ? "—" : `${formatScore(r.score)}${r.maximum_score !== null ? ` / ${formatScore(r.maximum_score)}` : ""}`}
                    {(r.field_breakdown || []).length > 0 && <span className="kp-sub">{r.field_breakdown.map((f) => `${f.label}: ${formatScore(f.value)}`).join(" · ")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="kp-muted mb-0">{fb.month.status === "AWAITING_FEEDBACK" ? "Awaiting User Feedback — no responses yet for this month." : "No feedback for this month."}</p>
      )}
    </section>
  );
}

const KpiDetailModal = ({ kpiId, kpiName, month, onClose, onEnter }) => {
  const [detail, setDetail] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/performance/my-kra-kpi/${kpiId}`, { params: { month } });
        const d = res.data?.data || null;
        let rows = [];
        if (d?.kpi?.data_source !== "AUTOMATIC") {
          const ev = await api.get(`/performance/my-kra-kpi/${kpiId}/evidence`, { params: { month } });
          rows = ev.data?.data || [];
        }
        if (!cancelled) { setDetail(d); setEvidence(rows); }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err?.response?.data?.error || "Could not load this KPI.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kpiId, month]);

  const kpi = detail?.kpi;
  // Full April-March year: computed months from the API, later months shown as not yet due.
  const yearRows = detail ? monthsForFiscalYear(detail.fiscal_year).map((m) => {
    const key = monthKeyFor(detail.fiscal_year, m.num);
    return { key, row: (detail.trend || []).find((t) => t.period_month === key) || null, isFuture: key > month };
  }) : [];

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable className="kp-modal">
      <Modal.Header closeButton>
        <Modal.Title as="div">
          <div className="tlp-eyebrow">KPI detail · {formatMonthKey(month)}</div>
          <div className="kp-modal-title">{kpiName}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="kp-center"><Spinner animation="border" size="sm" /> Loading…</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {kpi && !loading && (
          <>
            <div className="kp-detail-facts">
              <div><span>Weight</span><strong>{formatScore(kpi.weightage)}%</strong></div>
              <div><span>Calculation</span><strong>{CALCULATION_LABELS[kpi.calculation_type] || kpi.calculation_type}</strong></div>
              <div><span>Annual target</span><strong>{kpi.annual_target_label || formatScore(kpi.annual_target_value)}</strong></div>
              <div><span>YTD result</span><strong>{formatKpiValue(kpi.calculation_type, kpi.ytd)}</strong>
                {formatRatioParts(kpi.calculation_type, kpi.ytd) && <small className="kp-sub">{formatRatioParts(kpi.calculation_type, kpi.ytd)}</small>}</div>
              <div><span>Rating</span><strong>{kpi.rating === null ? "—" : `${kpi.rating} / 5`}</strong></div>
              <div><span>Weighted score</span><strong>{kpi.weighted_contribution === null ? "—" : formatScore(kpi.weighted_contribution)}</strong></div>
              <div><span>Source</span><Pill meta={DATA_SOURCE_META[kpi.data_source]} /></div>
              <div><span>Status</span><Pill meta={STATUS_META[kpi.status]} /></div>
            </div>

            {kpi.description && <p className="kp-description">{kpi.description}</p>}
            {kpi.calculation_type.startsWith("RATIO") && (
              <p className="kp-sub">Year-to-date ratio = sum of monthly numerators ÷ sum of monthly denominators × 100 — monthly percentages are never averaged.</p>
            )}

            <section className="kp-detail-section">
              <h6>Rating rule</h6>
              {detail.thresholds.length ? (
                <div className="kp-threshold-row">
                  {detail.thresholds.map((t) => (
                    <div key={t.rating} className={`kp-threshold ${kpi.rating === t.rating ? "active" : ""}`}>
                      <strong>{t.rating}</strong><span>{formatRange(t)}</span>
                    </div>
                  ))}
                </div>
              ) : kpi.manual_rating ? (
                // Phase 67: the reviewer's rating (latest rated month up to
                // the selected month); the count above stays the factual value.
                <div className="kp-manual-rating" data-testid="kp-manual-rating-detail">
                  <p className="mb-1">
                    Rated <strong>{kpi.manual_rating.rating} / 5</strong> by {kpi.manual_rating.reviewed_by_name || "the reviewer"}
                    {kpi.manual_rating.is_current_month ? "" : ` (rating given for ${formatMonthKey(kpi.manual_rating.period_month)})`}.
                  </p>
                  {kpi.manual_rating.remarks && <p className="kp-sub mb-0">Reviewer remarks: {kpi.manual_rating.remarks}</p>}
                </div>
              ) : (
                <p className="kp-muted mb-0">Rating rule pending — this KPI needs a manual rating by the reviewer; no score is generated automatically.</p>
              )}
            </section>

            {detail.tickets && <TicketDrilldown tickets={detail.tickets} />}

            <MonthContext kpi={kpi} context={detail.entry_context} month={month} />
            <FeedbackSection kpi={kpi} detail={detail} />

            <section className="kp-detail-section">
              <h6>April – March history</h6>
              <p className="kp-sub mb-2">Rating and weighted score are year-to-date as of each month end.</p>
              <div className="table-responsive kp-table-wrap">
                <Table className="tlp-table kp-table mb-0" size="sm">
                  <thead><tr><th>Month</th><th>Target</th><th>Actual</th><th>Rating</th><th>Weighted Score</th><th>Evidence Status</th></tr></thead>
                  <tbody>
                    {yearRows.map(({ key, row, isFuture }) => (
                      <tr key={key} className={isFuture ? "kp-row-future" : ""}>
                        <td data-label="Month">{formatMonthKey(key)}</td>
                        <td data-label="Target">{row?.month_target === null || row?.month_target === undefined ? "—" : formatScore(row.month_target)}</td>
                        <td data-label="Actual">
                          {!row ? <span className="kp-muted">{isFuture ? "Not yet due" : "—"}</span> : (
                            <>
                              {formatKpiValue(kpi.calculation_type, row.current_month)}
                              {formatRatioParts(kpi.calculation_type, row.current_month) && <span className="kp-sub">{formatRatioParts(kpi.calculation_type, row.current_month)}</span>}
                            </>
                          )}
                        </td>
                        <td data-label="Rating">{row?.rating === null || row?.rating === undefined ? "—" : row.rating}</td>
                        <td data-label="Weighted Score">{row?.weighted_contribution === null || row?.weighted_contribution === undefined ? "—" : formatScore(row.weighted_contribution)}</td>
                        <td data-label="Evidence Status">
                          {row?.evidence_status ? <Pill meta={EVIDENCE_STATUS_META[row.evidence_status]} />
                            : <span className="kp-muted">{row && kpi.data_source === "AUTOMATIC" ? "Automatic" : "—"}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>

            {kpi.data_source !== "AUTOMATIC" && (
              <section className="kp-detail-section">
                <div className="kp-detail-section-head">
                  <h6>My entries</h6>
                  {kpi.can_enter_data && detail.entry_form?.supported && (
                    <button type="button" className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={onEnter}>Enter Monthly Data</button>
                  )}
                </div>
                {detail.entry_form && !detail.entry_form.supported && <p className="kp-muted">{detail.entry_form.reason}</p>}
                {evidence.length ? (
                  <ul className="kp-evidence-list">
                    {evidence.map((e) => (
                      <li key={e.id}>
                        <div>
                          <strong>{formatMonthKey(e.period_month)}</strong>
                          <span className="kp-sub">
                            {e.raw_numerator !== null && e.raw_denominator !== null
                              ? `${formatScore(e.raw_numerator)} / ${formatScore(e.raw_denominator)}`
                              : e.raw_count !== null ? `Count ${formatScore(e.raw_count)}` : ""}
                            {e.details_json?.notes ? ` · ${e.details_json.notes}` : ""}
                          </span>
                          {(e.attachments || []).map((a) => (
                            <span key={a.id} className="kp-sub">📎 {a.original_name}</span>
                          ))}
                        </div>
                        <span className="kp-pill kp-pill-gray">{EVIDENCE_STATUS_LABELS[e.status] || e.status}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="kp-muted mb-0">No entries yet.</p>}
              </section>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default KpiDetailModal;
